/**
 * `<LedgerCell>` — one inline-editable analytics variable.
 *
 * ── Interaction model ────────────────────────────────────────────────────────
 * Display state is a BUTTON, not a bare input. That is a deliberate
 * accessibility choice: rendering 11 live inputs per row would put ~1,000 tab
 * stops on a 90-row ledger, and a keyboard user would never get past the first
 * screen. One tab stop per cell; activating it swaps in the real control.
 *
 *   Tab / Shift-Tab  move between cells
 *   Enter or Space   open the editor (the control autofocuses)
 *   Escape           cancel — the draft is discarded and focus returns to the cell
 *   Blur             SAVE (only when the value actually changed)
 *
 * Blur-to-save is the founder's call: no modal, no Save button. The guard is
 * that an unchanged value never fires a request, so tabbing across a row is
 * free.
 *
 * ── Clearing ─────────────────────────────────────────────────────────────────
 * Emptying the control sends an explicit `null`, which the backend treats as
 * "clear this column". A cleared cell is a first-class edit, not a no-op — this
 * is how a recruiter undoes a typo.
 *
 * ── Token discipline ─────────────────────────────────────────────────────────
 * Semantic token utilities only; no raw hex/rgb. The focus ring is the shared
 * `--border-focus`, so keyboard position is visible in light and dark.
 */
import { useEffect, useRef, useState, type JSX, type KeyboardEvent } from "react";
import { cn } from "@/lib/utils";
import {
  formatLedgerValue,
  toControlValue,
  toPatchValue,
  type LedgerField,
} from "./ledgerFields";

interface LedgerCellProps {
  field: LedgerField;
  value: string | boolean | null;
  /** Row label, used to make the cell's accessible name unambiguous. */
  candidateName: string;
  /** Called with the JSON value to PATCH (null means clear). */
  onCommit: (value: string | boolean | null) => void;
  /** Set when the last write for this cell failed — paints an error ring. */
  errorMessage?: string | null;
  disabled?: boolean;
  testId?: string;
}

const CONTROL_CLS = cn(
  "w-full rounded-[var(--radius)] border border-border-default bg-surface-card",
  "text-text-primary outline-none",
  "focus-visible:ring-2 focus-visible:ring-border-focus",
);

export function LedgerCell({
  field,
  value,
  candidateName,
  onCommit,
  errorMessage,
  disabled = false,
  testId,
}: LedgerCellProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(() => toControlValue(field, value));
  // Set while Escape is unwinding, so the blur it triggers does not save.
  const cancelledRef = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const controlRef = useRef<HTMLInputElement | HTMLSelectElement>(null);

  const committed = toControlValue(field, value);

  // Re-sync the draft whenever the row's value changes underneath us (an
  // optimistic update landing, or a refetch), but never while the user is
  // mid-edit — that would yank the text out from under them.
  useEffect(() => {
    if (!editing) setDraft(committed);
  }, [committed, editing]);

  useEffect(() => {
    if (editing) controlRef.current?.focus();
  }, [editing]);

  const open = (): void => {
    if (disabled) return;
    cancelledRef.current = false;
    setDraft(committed);
    setEditing(true);
  };

  const cancel = (): void => {
    cancelledRef.current = true;
    setDraft(committed);
    setEditing(false);
    // Return focus to the cell so keyboard position is never lost.
    requestAnimationFrame(() => buttonRef.current?.focus());
  };

  const commit = (): void => {
    setEditing(false);
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    // Unchanged → no request. Makes tabbing across a row free.
    if (draft === committed) return;
    onCommit(toPatchValue(field, draft));
  };

  const onControlKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      cancel();
      return;
    }
    if (e.key === "Enter" && field.kind !== "enum" && field.kind !== "bool") {
      e.preventDefault();
      // Blur commits; this just gets us there without reaching for the mouse.
      controlRef.current?.blur();
    }
  };

  const onButtonKeyDown = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open();
    }
  };

  const display = formatLedgerValue(field, value);
  const accessibleName = `${field.label} for ${candidateName}`;

  if (editing) {
    const shared = {
      ref: controlRef as never,
      className: cn(CONTROL_CLS, "h-8 px-[6px] text-[12.5px]"),
      onBlur: commit,
      onKeyDown: onControlKeyDown,
      "aria-label": accessibleName,
      "data-testid": testId ? `${testId}-input` : undefined,
    };

    if (field.kind === "bool" || field.kind === "enum") {
      const options =
        field.kind === "bool"
          ? [
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ]
          : (field.options ?? []);
      return (
        <select
          {...shared}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        >
          <option value="">— Not recorded</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        {...shared}
        type="date"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
    );
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={open}
      onKeyDown={onButtonKeyDown}
      disabled={disabled}
      aria-label={
        display ? `${accessibleName}: ${display}` : `${accessibleName}: not recorded`
      }
      title={errorMessage ?? field.help}
      data-testid={testId}
      data-ledger-cell={field.key}
      className={cn(
        "h-8 w-full rounded-[var(--radius)] px-[6px] text-left text-[12.5px] transition-colors",
        "border border-transparent",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus",
        !disabled && "hover:border-border-default hover:bg-surface-subtle/60",
        disabled && "cursor-default",
        display ? "text-text-primary" : "text-text-tertiary",
        errorMessage && "border-[hsl(var(--weak))] bg-[hsl(var(--weak-soft))]",
      )}
    >
      {display ?? "—"}
    </button>
  );
}
